import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CreateSubscriptionActions, loadScript } from '@paypal/paypal-js';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { AuthService, UserModel } from '@modules/auth';
import { SubscriptionService } from '@core/_services/subscription/subscription.service';
import { PaymentSubscriptionStatus } from '@core/models/interfaces/subscription.interface';

@Component({
  selector: 'app-subscription-modal',
  templateUrl: './subscription-modal.component.html',
  styleUrls: ['./subscription-modal.component.scss'],
})
export class SubscriptionModalComponent implements OnInit, AfterViewInit {
  status: PaymentSubscriptionStatus;
  paymentDate: string;
  currentUser: UserModel = this.authService.currentUserValue;
  isCanceling: boolean = false;

  @ViewChild('paypalButtonContainer') paypalButtonContainer!: ElementRef<HTMLDivElement>;

  protected readonly PaymentSubscriptionStatus = PaymentSubscriptionStatus;

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private subscriptionService: SubscriptionService,
    private dialogRef: MatDialogRef<SubscriptionModalComponent>
  ) {
  }

  get canDismiss(): boolean {
    return !this.dialogRef.disableClose;
  }

  async ngOnInit(): Promise<void> {
    this.initVariables();
  }

  async ngAfterViewInit(): Promise<void> {
    // Ensure that the PayPal button is initialized after the view is fully loaded
    if (
      (this.status === PaymentSubscriptionStatus.INACTIVE || this.status === PaymentSubscriptionStatus.TRIAL)
      && this.paypalButtonContainer
    ) {
      await this.initPaypal();
    }
  }

  initVariables(): void {
    this.status = this.currentUser?.profile?.company?.subscription?.status ?? PaymentSubscriptionStatus.INACTIVE;
    this.paymentDate = this.currentUser?.profile?.company?.subscription?.valid_until ?? new Date().toISOString();
  }

  async initPaypal(): Promise<void> {
    const paypal = await loadScript({
      clientId: environment.paypal.clientId,
      vault: true,
      intent: 'subscription',
      components: 'buttons',
    });

    if (!paypal) {
      console.error('PayPal SDK failed to load.');
      return;
    }

    await paypal.Buttons({
      style: {
        shape: 'pill',
        color: 'gold',
        layout: 'vertical',
        label: 'subscribe',
      },
      fundingSource: 'paypal',
      createSubscription: (_data: Record<string, unknown>, actions: CreateSubscriptionActions): Promise<string> => {
        return actions.subscription.create({
          plan_id: environment.paypal.planId,
          custom_id: `user_id:${ this.currentUser.profile.id }`
        });
      },
      onApprove: (): Promise<void> => {
        // Mark payment as started and show processing modal
        this.subscriptionService.startPayment().subscribe({
          next: () => {
            this.dismiss();
            this.subscriptionService.openPaymentProcessingModal();
          },
          error: (error) => {
            console.error('Failed to mark payment as started:', error);

            this.authService.getUserByToken();
            this.dismiss();
          }
        });
        return Promise.resolve();
      }
    }).render(this.paypalButtonContainer.nativeElement);
  }

  shouldShowSubscribeButton(): boolean {
    const subscription = this.currentUser?.profile?.company?.subscription;
    this.status = subscription?.status ?? PaymentSubscriptionStatus.INACTIVE;

    // Show subscribe button if status is trial
    if (this.status === PaymentSubscriptionStatus.TRIAL) {
      return true;
    }

    // Show subscribe button if the status is inactive and valid date has passed
    if (this.status === PaymentSubscriptionStatus.INACTIVE) {
      if (!subscription?.valid_until) {
        // If there is no valid until date, show the button
        return true;
      }

      const validUntilDate = new Date(subscription.valid_until);
      const currentDate = new Date();

      // Calculate time difference in milliseconds
      const timeDiff = currentDate.getTime() - validUntilDate.getTime();

      // Show button if the valid date has passed
      return timeDiff > 0;
    }

    // Don't show button for other statuses (like active)
    return false;
  }

  shouldShowPaymentDate(): boolean {
    // Show date if status is not inactive
    if (this.status !== PaymentSubscriptionStatus.INACTIVE) {
      return true;
    }

    // If status is inactive, show date only if payment date is not due (still valid)
    if (this.status === PaymentSubscriptionStatus.INACTIVE && this.paymentDate) {
      const validUntilDate = new Date(this.paymentDate);
      const currentDate = new Date();

      // Calculate time difference in milliseconds
      const timeDiff = currentDate.getTime() - validUntilDate.getTime();

      // Show date if the valid date has NOT passed yet (timeDiff <= 0)
      return timeDiff <= 0;
    }

    return false;
  }

  cancelSubscription() {
    this.isCanceling = true;
    this.subscriptionService.cancelSubscription().subscribe({
      next: () => {
        this.authService.getUserByToken();
        this.dismiss();
      }
    });
  }

  dismiss(): void {
    this.dialog.closeAll();
  }
}
